import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import { TabsContent } from 'components/ui/tabs';
import FinalizedHours from 'pages/dashboard/finalized-hours/FinalizedHours';
import PeriodPicker, { PeriodPickerInHeader } from 'pages/dashboard/finalized-hours/PeriodPicker';
import ProgressTracking from 'pages/dashboard/progress-tracking/ProgressTracking';
import { ReactNode, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DashboardForm, DashboardHoursTab } from 'types/dashboard';
import { FeatureFlag } from 'types/features';
import type { HomeWidgetSize } from 'types/homeWidgets';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import HomeSection, { HOME_TAB_CLASS, HOME_TAB_LIST_CLASS } from './HomeSection';

/**
 * The dashboard's hours card as one Home card: the period picker in its header, then Finalised hours and, where the
 * tenant has it, Progress tracking. Reads the period from the page's `DashboardForm`; the ring is laid out for `size`.
 */
export default function MyTimeSection({ size, actions }: { size: HomeWidgetSize; actions?: ReactNode }) {
    const isProgressTrackingEnabled = useIsFeatureEnabled(FeatureFlag.DASHBOARD_PROGRESS_TRACKING);
    // The tab reads the user's own employee record, which takes READ.EMPLOYEES.
    const canReadEmployees = useHasPermissions(['READ.EMPLOYEES']);
    const { control, setValue } = useFormContext<DashboardForm>();
    const tab = useWatch({ control, name: 'hoursTab' }) ?? 'finalized-hours';
    // Mounted from its first visit on, as on the dashboard: the current employee is not cached once unsubscribed.
    const [progressVisited, setProgressVisited] = useState(tab === 'progress-tracking');

    return (
        <PeriodPickerInHeader value>
            <HomeSection
                title={t('home.my-time')}
                actions={
                    <>
                        {actions}
                        <PeriodPicker inHeader />
                    </>
                }
                className="gap-3 pb-[18px]"
                data-tour="home-my-time"
            >
                {isProgressTrackingEnabled && canReadEmployees ? (
                    <LineTabs
                        value={tab}
                        className="flex min-h-0 flex-1 flex-col"
                        onValueChange={(value: DashboardHoursTab) => {
                            setValue('hoursTab', value);
                            if (value === 'progress-tracking') {
                                setProgressVisited(true);
                            }
                        }}
                    >
                        <LineTabsList className={HOME_TAB_LIST_CLASS}>
                            <LineTab value="finalized-hours" size="compact" className={HOME_TAB_CLASS}>
                                {t('dashboard.finalized-hours')}
                            </LineTab>
                            <LineTab value="progress-tracking" size="compact" className={HOME_TAB_CLASS}>
                                {t('dashboard.progress-tracking.title')}
                            </LineTab>
                        </LineTabsList>
                        <TabsContent value="finalized-hours" className="min-h-0 overflow-y-auto pt-3">
                            <FinalizedHours home size={size} />
                        </TabsContent>
                        <TabsContent
                            value="progress-tracking"
                            keepMounted={progressVisited}
                            className="min-h-0 overflow-y-auto"
                        >
                            {/* Its bar and footer band hang from a fixed height, as in the dashboard's card. */}
                            <div className="h-60">
                                <ProgressTracking />
                            </div>
                        </TabsContent>
                    </LineTabs>
                ) : (
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        <FinalizedHours home size={size} />
                    </div>
                )}
            </HomeSection>
        </PeriodPickerInHeader>
    );
}
